/**
 * Painel de execução técnica de UM item da O.S.: o que foi encontrado, o que foi
 * feito e como ficou — por componente ou por posição, conforme a configuração do item.
 *
 * Abre dentro da própria O.S. (sem tela desconectada). Primeiro mostra a última
 * manutenção do mesmo componente/posição; depois os campos configurados, a condição
 * (calculada pelos limites, quando houver), a intervenção, o antes × depois e a
 * pendência. O item só vira ✓ quando todas as posições foram avaliadas e nenhum
 * problema ficou sem destino — quem decide é o servidor.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  MaintenanceExecFieldDto, MaintenanceInspectionConditionDto, MaintenanceInspectionDto, MaintenanceInspectionPanelDto,
  MaintenanceInterventionDto, MaintenanceTechnicalHistoryDto,
} from "@bi/types";
import { api, ApiError } from "../../lib/api";
import { fmtNumber } from "../../lib/format";
import { EvidenceThumb } from "./EvidenceThumbs";
import { PositionMap, type PositionCell } from "./PositionMap";
import { AxleExecution, TilesExecution } from "./ExecutionScreens";
import {
  ALL_INTERVENTIONS, COND_LABEL, COND_STYLE, INTERVENTION_LABEL, autoCondition, classifyField, deltasOf, fmtDiff, fmtVal,
} from "./work-order-inspection-ui";

interface Form {
  condition: MaintenanceInspectionConditionDto | null;
  interventions: MaintenanceInterventionDto[];
  measures: Record<string, unknown>;
  notes: string;
  beforeAfter: boolean;
  before: { condition: MaintenanceInspectionConditionDto | null; measures: Record<string, unknown> };
  after: { condition: MaintenanceInspectionConditionDto | null; measures: Record<string, unknown> };
  pendingReason: string;
  pendingOwner: string;
  pendingDueAt: string;
}

const vazio = (): Form => ({
  condition: null, interventions: [], measures: {}, notes: "", beforeAfter: false,
  before: { condition: null, measures: {} }, after: { condition: null, measures: {} },
  pendingReason: "", pendingOwner: "", pendingDueAt: "",
});

const deInspecao = (i: MaintenanceInspectionDto | undefined): Form =>
  !i
    ? vazio()
    : {
        condition: i.condition === "not_inspected" ? null : i.condition,
        interventions: i.interventions, measures: { ...(i.measures ?? {}) }, notes: i.notes ?? "",
        beforeAfter: !!(i.before || i.after),
        before: { condition: i.before?.condition ?? null, measures: { ...(i.before?.measures ?? {}) } },
        after: { condition: i.after?.condition ?? null, measures: { ...(i.after?.measures ?? {}) } },
        pendingReason: i.pendingReason ?? "", pendingOwner: i.pendingOwner ?? "",
        pendingDueAt: i.pendingDueAt ? i.pendingDueAt.slice(0, 10) : "",
      };

const fmtDia = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

interface PainelProps { workOrderId: string; itemId: string; podeEditar: boolean; responsavel?: string | null; onFechar: () => void; onMudou: () => void }

/** Escolhe a apresentação pelo `layout` configurado no item: cartões, eixos da carreta ou o painel padrão. */
export function TechnicalItemPanel(props: PainelProps) {
  const { data } = useQuery({
    queryKey: ["maintenance", "inspection-panel", props.workOrderId, props.itemId],
    queryFn: () => api.get<MaintenanceInspectionPanelDto>(`/maintenance/work-orders/${props.workOrderId}/checklist-items/${props.itemId}/inspection`),
  });
  const layout = data?.config.layout;
  if (layout === "tiles") return <TilesExecution {...props} responsavel={props.responsavel ?? null} />;
  if (layout === "axle_map") return <AxleExecution {...props} responsavel={props.responsavel ?? null} />;
  return <DefaultExecution {...props} />;
}

function DefaultExecution({
  workOrderId, itemId, podeEditar, onFechar, onMudou,
}: PainelProps) {
  const qc = useQueryClient();
  const chave = ["maintenance", "inspection-panel", workOrderId, itemId];
  const painel = useQuery({
    queryKey: chave,
    queryFn: () => api.get<MaintenanceInspectionPanelDto>(`/maintenance/work-orders/${workOrderId}/checklist-items/${itemId}/inspection`),
  });
  const p = painel.data;
  const [sel, setSel] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(vazio());
  const [erro, setErro] = useState<string | null>(null);
  const fotoRef = useRef<HTMLInputElement>(null);
  const [legendaFoto, setLegendaFoto] = useState("Foto");
  const [verHistorico, setVerHistorico] = useState(false);

  const fechada = p ? p.workOrder.status === "completed" || p.workOrder.status === "cancelled" : true;
  const editavel = podeEditar && !fechada;
  const cfg = p?.config;
  const porPosicao = useMemo(() => new Map((p?.inspections ?? []).map((i) => [i.positionKey, i])), [p]);
  const posicional = !!p && p.positions.length > 0 && cfg?.type !== "measurement" && cfg?.type !== "component";
  const chaves = posicional ? p!.positions.map((x) => x.key) : [""];

  // Seleciona a primeira posição ainda não avaliada (ou a primeira) quando o painel carrega.
  useEffect(() => {
    if (!p || sel !== null) return;
    setSel(chaves.find((k) => (porPosicao.get(k)?.condition ?? "not_inspected") === "not_inspected") ?? chaves[0] ?? "");
  }, [p]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (sel === null) return;
    setForm(deInspecao(porPosicao.get(sel)));
    setErro(null);
  }, [sel, p]); // eslint-disable-line react-hooks/exhaustive-deps

  const salvar = useMutation({
    mutationFn: (v: { key: string; f: Form }) =>
      api.put(`/maintenance/work-orders/${workOrderId}/checklist-items/${itemId}/inspection`, corpo(v.key, v.f)),
    onSuccess: () => { setErro(null); qc.invalidateQueries({ queryKey: chave }); onMudou(); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível salvar."),
  });
  const enviarFoto = useMutation({
    mutationFn: (v: { inspectionId: string; file: File }) =>
      api.uploadWithFields("/maintenance/attachments", v.file, { scope: "work_order_inspection", inspectionId: v.inspectionId, caption: legendaFoto }),
    onSuccess: () => { setErro(null); qc.invalidateQueries({ queryKey: chave }); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível enviar a foto."),
  });

  // Evolução do componente (O.S. concluídas) — só busca quando a pessoa abre.
  const historico = useQuery({
    queryKey: ["maintenance", "technical-history", p?.workOrder.vehiclePlate, p?.item.itemKey, sel],
    queryFn: () => api.get<MaintenanceTechnicalHistoryDto>(
      `/maintenance/vehicles/${encodeURIComponent(p!.workOrder.vehiclePlate)}/technical-history?itemKey=${encodeURIComponent(p!.item.itemKey ?? "")}&positionKey=${encodeURIComponent(sel ?? "")}&limit=12`),
    enabled: verHistorico && !!p?.item.itemKey && sel !== null,
  });

  if (painel.isLoading || !p || !cfg) return <div className="rounded-lg border border-surface-border p-4 text-sm text-gray-500">Carregando execução técnica…</div>;

  const atual = sel !== null ? porPosicao.get(sel) : undefined;
  const ant = sel !== null ? p.previous[sel] : undefined;
  const auto = autoCondition(cfg, form.measures);
  const condicao = auto ?? form.condition;
  const interv = cfg.interventions?.length ? cfg.interventions : ALL_INTERVENTIONS;
  const problema = condicao === "attention" || condicao === "critical";
  const interveio = form.interventions.some((i) => ["adjusted", "repaired", "replaced", "lubricated", "cleaned", "tightened", "other"].includes(i));
  const posAtual = p.positions.find((x) => x.key === sel);
  const avaliadas = chaves.filter((k) => (porPosicao.get(k)?.condition ?? "not_inspected") !== "not_inspected").length;

  const principal = cfg.fields.find((f) => f.type === "number" || f.type === "measurement");
  const celulas: PositionCell[] = p.positions.map((pos) => {
    const i = porPosicao.get(pos.key);
    return {
      pos, condition: i?.condition ?? "not_inspected",
      value: i && principal ? (i.measures?.[principal.name] != null ? fmtVal(i.measures[principal.name], principal.unit) : null) : null,
      intervened: !!i?.interventions.some((x) => !["no_action", "pending", "not_applicable"].includes(x)),
    };
  });

  const setMedida = (nome: string, v: unknown) => setForm((f) => ({ ...f, measures: { ...f.measures, [nome]: v } }));
  const alternar = (iv: MaintenanceInterventionDto) =>
    setForm((f) => {
      const tem = f.interventions.includes(iv);
      if (tem) return { ...f, interventions: f.interventions.filter((x) => x !== iv) };
      if (iv === "no_action") return { ...f, interventions: ["no_action"] };
      return { ...f, interventions: [...f.interventions.filter((x) => x !== "no_action"), iv] };
    });

  const deltas = ant ? deltasOf(cfg, { measures: ant.measures, odometerKm: ant.odometerKm }, { measures: form.measures, odometerKm: p.workOrder.odometerKm }) : [];
  const kmDesde = ant?.odometerKm != null && p.workOrder.odometerKm != null && p.workOrder.odometerKm >= ant.odometerKm ? p.workOrder.odometerKm - ant.odometerKm : null;
  const pode = editavel && !salvar.isPending;
  const proxima = () => {
    const i = chaves.indexOf(sel ?? "");
    setSel(chaves[(i + 1) % chaves.length] ?? "");
  };
  // "Repetir": só quando não há medida obrigatória (cada posição tem a sua medida).
  const podeRepetir = posicional && editavel && !cfg.fields.some((f) => (f.type === "number" || f.type === "measurement") && f.required);

  return (
    <section className="space-y-3 rounded-xl border border-tm-100 bg-white p-3 sm:p-4" aria-label={`Execução técnica: ${p.item.itemName}`}>
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="font-semibold text-gray-900">{p.item.itemName}</h4>
          <p className="text-sm text-gray-500">
            Veículo <span className="font-mono">{p.workOrder.vehiclePlate}</span>
            {p.workOrder.odometerKm != null ? ` · ${fmtNumber(p.workOrder.odometerKm)} km` : ""}
            {posicional ? ` · ${avaliadas} de ${chaves.length} posições avaliadas` : ""}
          </p>
          {ant && (
            <p className="text-xs text-gray-500">
              Última inspeção: {fmtDia(ant.date)}{ant.odometerKm != null ? `, com ${fmtNumber(ant.odometerKm)} km` : ""}
              {ant.workOrderNumber != null ? ` · O.S. nº ${ant.workOrderNumber}` : ""}
            </p>
          )}
        </div>
        <button type="button" className="btn-ghost text-sm" onClick={onFechar}>Fechar</button>
      </header>

      {posicional && (
        <>
          <div className="hidden md:block"><PositionMap cells={celulas} selected={sel} onSelect={setSel} /></div>
          <div className="flex items-center gap-2 md:hidden">
            <select className="input flex-1" value={sel ?? ""} onChange={(e) => setSel(e.target.value)} aria-label="Posição">
              {p.positions.map((x) => {
                const c = porPosicao.get(x.key)?.condition ?? "not_inspected";
                return <option key={x.key} value={x.key}>{x.label}: {COND_LABEL[c]}</option>;
              })}
            </select>
            <button type="button" className="btn-secondary min-h-[2.75rem]" onClick={proxima}>Próxima ▸</button>
          </div>
        </>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        {/* Última manutenção × atual */}
        <div className="rounded-lg border border-surface-border">
          <div className="grid grid-cols-2 divide-x divide-surface-border text-sm">
            <div className="p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Última manutenção</p>
              {ant ? (
                <>
                  <p className="text-gray-800">{fmtDia(ant.date)}{ant.odometerKm != null ? ` · ${fmtNumber(ant.odometerKm)} km` : ""}</p>
                  <p className="mt-1"><span className={COND_STYLE[ant.condition].badge}>{COND_LABEL[ant.condition]}</span></p>
                  <p className="mt-1 text-xs text-gray-500">Intervenção: {ant.interventions.length ? ant.interventions.map((i) => INTERVENTION_LABEL[i]).join(", ") : "Nenhuma"}</p>
                </>
              ) : <p className="text-gray-400">Sem histórico deste componente.</p>}
            </div>
            <div className="p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Manutenção atual</p>
              <p className="text-gray-800">{p.workOrder.odometerKm != null ? `${fmtNumber(p.workOrder.odometerKm)} km` : "—"}</p>
              <p className="mt-1">{condicao ? <span className={COND_STYLE[condicao].badge}>{COND_LABEL[condicao]}</span> : <span className="text-xs text-gray-400">Aguardando registro</span>}</p>
            </div>
          </div>
          {ant && cfg.fields.some((f) => f.type === "number" || f.type === "measurement") && (
            <table className="w-full border-t border-surface-border text-sm">
              <tbody className="divide-y divide-gray-100">
                {cfg.fields.filter((f) => f.type === "number" || f.type === "measurement").map((f) => {
                  const d = deltas.find((x) => x.field === f.name);
                  return (
                    <tr key={f.name}>
                      <td className="px-3 py-1.5 text-gray-600">{f.label}</td>
                      <td className="px-3 py-1.5">{fmtVal(ant.measures?.[f.name], f.unit)} → <span className="font-medium">{fmtVal(form.measures[f.name], f.unit)}</span></td>
                      <td className={`px-3 py-1.5 text-right text-xs ${d && d.diff < 0 ? "text-danger-600" : "text-gray-500"}`}>
                        {d ? `Variação ${fmtDiff(d.diff)}${f.unit ? ` ${f.unit}` : ""}` : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {ant && kmDesde != null && <p className="border-t border-surface-border px-3 py-1.5 text-xs text-gray-500">Desde a última preventiva: {fmtNumber(kmDesde)} km rodados</p>}
        </div>

        {/* Registro */}
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-900">{posAtual ? posAtual.label : "Registro do componente"}
            {posAtual?.tire?.code && <span className="ml-2 text-xs font-normal text-gray-500">Pneu #{posAtual.tire.code}{posAtual.tire.life ? ` · ${posAtual.tire.life}` : ""}</span>}
          </p>

          {cfg.fields.map((f) => (
            <Campo key={f.name} f={f} valor={form.measures[f.name]} disabled={!pode} onChange={(v) => setMedida(f.name, v)}
              classe={classifyField(f, form.measures[f.name])} />
          ))}

          <div>
            <p className="mb-1 text-xs font-medium text-gray-500">Condição encontrada</p>
            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Condição">
              {(["ok", "attention", "critical"] as const).map((c) => {
                const bloqueada = !!auto && c !== auto;
                return (
                  <button key={c} type="button" role="radio" aria-checked={condicao === c} disabled={!pode || bloqueada}
                    onClick={() => setForm((f) => ({ ...f, condition: c }))}
                    className={`min-h-[2.5rem] rounded-lg border px-3 text-sm disabled:opacity-40 ${condicao === c ? `border-2 ${COND_STYLE[c].ring} bg-surface-muted font-medium` : "border-surface-border"}`}>
                    {COND_STYLE[c].symbol} {COND_LABEL[c]}
                  </button>
                );
              })}
            </div>
            {auto && <p className="mt-1 text-xs text-gray-500">Calculada pelos limites configurados para este item.</p>}
            {problema && auto && (
              <p className="mt-1 rounded-md border border-warning-400/40 bg-warning-50 px-2 py-1 text-xs text-warning-700">
                {auto === "critical" ? "Crítico" : "Atenção"}: a medida atual está além do limite configurado para este item.
              </p>
            )}
          </div>

          <div>
            <p className="mb-1 text-xs font-medium text-gray-500">Intervenção</p>
            <div className="flex flex-wrap gap-1.5">
              {interv.map((iv) => (
                <button key={iv} type="button" aria-pressed={form.interventions.includes(iv)} disabled={!pode} onClick={() => alternar(iv)}
                  className={`min-h-[2.5rem] rounded-lg border px-3 text-sm disabled:opacity-40 ${form.interventions.includes(iv) ? "border-tm-600 bg-tm-50 font-medium text-tm-700" : "border-surface-border"}`}>
                  {INTERVENTION_LABEL[iv]}
                </button>
              ))}
            </div>
            {problema && form.interventions.length === 0 && <p className="mt-1 text-xs text-danger-600">Dê um destino ao problema: registre a intervenção ou deixe pendente.</p>}
          </div>

          {form.interventions.includes("pending") && (
            <div className="space-y-2 rounded-lg border border-warning-400/40 bg-warning-50 p-2.5">
              <input className="input w-full" placeholder="Motivo da pendência (obrigatório)" maxLength={500} disabled={!pode}
                value={form.pendingReason} onChange={(e) => setForm({ ...form, pendingReason: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <input className="input" placeholder="Responsável" maxLength={120} disabled={!pode} value={form.pendingOwner} onChange={(e) => setForm({ ...form, pendingOwner: e.target.value })} />
                <input type="date" className="input" aria-label="Prazo" disabled={!pode} value={form.pendingDueAt} onChange={(e) => setForm({ ...form, pendingDueAt: e.target.value })} />
              </div>
            </div>
          )}

          {(interveio || cfg.requiresBeforeAfter) && (
            <div className="rounded-lg border border-surface-border p-2.5">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.beforeAfter || !!cfg.requiresBeforeAfter} disabled={!pode || !!cfg.requiresBeforeAfter}
                  onChange={(e) => setForm({ ...form, beforeAfter: e.target.checked })} />
                Registrar antes × depois{cfg.requiresBeforeAfter ? " (obrigatório neste item)" : ""}
              </label>
              {(form.beforeAfter || cfg.requiresBeforeAfter) && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {(["before", "after"] as const).map((lado) => (
                    <div key={lado} className="space-y-1.5">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{lado === "before" ? "Antes" : "Depois"}</p>
                      <select className="input w-full" disabled={!pode} value={form[lado].condition ?? ""}
                        onChange={(e) => setForm({ ...form, [lado]: { ...form[lado], condition: (e.target.value || null) as MaintenanceInspectionConditionDto | null } })}>
                        <option value="">Condição…</option>
                        {(["ok", "attention", "critical"] as const).map((c) => <option key={c} value={c}>{COND_LABEL[c]}</option>)}
                      </select>
                      {cfg.fields.filter((f) => f.type === "number" || f.type === "measurement").map((f) => (
                        <input key={f.name} className="input w-full" inputMode="decimal" disabled={!pode} placeholder={`${f.label}${f.unit ? ` (${f.unit})` : ""}`}
                          value={String(form[lado].measures[f.name] ?? "")}
                          onChange={(e) => setForm({ ...form, [lado]: { ...form[lado], measures: { ...form[lado].measures, [f.name]: e.target.value.replace(",", ".") } } })} />
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <textarea className="input w-full" rows={2} maxLength={1000} disabled={!pode} value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder={cfg.requiresObservationOnFailure && problema ? "Observação (obrigatória para atenção/crítico)" : "Observação (opcional)"} />

          {atual && (
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                {atual.attachments.map((a) => <EvidenceThumb key={a.id} anexo={{ id: a.id, fileName: a.fileName, mimeType: a.mimeType, caption: a.caption }} size="sm" />)}
                {atual.attachments.length === 0 && <span className="text-xs text-gray-400">{cfg.requiresPhoto ? "Foto obrigatória para problema/intervenção." : "Sem fotos."}</span>}
              </div>
              {editavel && (
                <div className="flex flex-wrap items-center gap-2">
                  <select className="input w-auto" aria-label="Tipo da foto" value={legendaFoto} onChange={(e) => setLegendaFoto(e.target.value)}>
                    <option>Foto</option><option>Antes da intervenção</option><option>Depois da intervenção</option>
                  </select>
                  <input ref={fotoRef} type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) enviarFoto.mutate({ inspectionId: atual.id, file: f }); e.target.value = ""; }} />
                  <button type="button" className="btn-secondary" disabled={enviarFoto.isPending} onClick={() => fotoRef.current?.click()}>
                    {enviarFoto.isPending ? "Enviando…" : "Anexar foto"}
                  </button>
                </div>
              )}
            </div>
          )}
          {!atual && editavel && cfg.requiresPhoto && <p className="text-xs text-gray-500">Salve o registro para anexar fotos.</p>}

          <div className="rounded-lg border border-surface-border">
            <button type="button" className="flex w-full items-center justify-between px-3 py-2 text-sm text-gray-700" aria-expanded={verHistorico} onClick={() => setVerHistorico(!verHistorico)}>
              <span>Evolução deste componente{posAtual ? ` (${posAtual.label})` : ""}</span><span className="text-gray-400">{verHistorico ? "▾" : "▸"}</span>
            </button>
            {verHistorico && (
              <div className="border-t border-surface-border px-3 py-2 text-sm">
                {historico.isLoading && <p className="text-gray-500">Carregando…</p>}
                {historico.data && historico.data.rows.length === 0 && <p className="text-gray-400">Ainda não há O.S. concluída com este componente.</p>}
                <ul className="divide-y divide-gray-100">
                  {historico.data?.rows.map((h) => (
                    <li key={h.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                      <span className="text-gray-700">{fmtDia(h.date)}{h.odometerKm != null ? ` · ${fmtNumber(h.odometerKm)} km` : ""}</span>
                      <span className="flex flex-wrap items-center gap-1.5 text-xs text-gray-600">
                        {principal && h.measures?.[principal.name] != null && <span>{fmtVal(h.measures[principal.name], principal.unit)}</span>}
                        <span className={COND_STYLE[h.condition].badge}>{COND_LABEL[h.condition]}</span>
                        {h.interventions.some((x) => x !== "no_action") && <span>→ {h.interventions.filter((x) => x !== "no_action").map((x) => INTERVENTION_LABEL[x].toLowerCase()).join(", ")}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {erro && <p className="rounded-lg border border-danger-600/30 bg-danger-50 p-2 text-sm text-danger-600">{erro}</p>}

          {editavel ? (
            <div className="flex flex-wrap gap-2">
              <button type="button" className="btn-primary min-h-[2.75rem]" disabled={!pode || condicao === null}
                onClick={() => sel !== null && salvar.mutate({ key: sel, f: form })}>
                {salvar.isPending ? "Salvando…" : posicional ? "Salvar posição" : "Salvar"}
              </button>
              {posicional && <button type="button" className="btn-secondary min-h-[2.75rem]" onClick={proxima}>Próxima posição</button>}
              {podeRepetir && atual && (
                <button type="button" className="btn-ghost min-h-[2.75rem]" disabled={salvar.isPending}
                  onClick={async () => {
                    const outras = chaves.filter((k) => k !== sel && (porPosicao.get(k)?.condition ?? "not_inspected") === "not_inspected");
                    for (const k of outras) await salvar.mutateAsync({ key: k, f: form }).catch(() => undefined);
                  }}>
                  Repetir nas outras posições
                </button>
              )}
            </div>
          ) : <p className="text-xs text-gray-500">Registro somente para consulta{fechada ? " (O.S. encerrada)" : ""}.</p>}
        </div>
      </div>
    </section>
  );
}

function Campo({
  f, valor, disabled, onChange, classe,
}: { f: MaintenanceExecFieldDto; valor: unknown; disabled: boolean; onChange: (v: unknown) => void; classe: "ok" | "attention" | "critical" | null }) {
  const rotulo = <span className="mb-0.5 block text-xs font-medium text-gray-500">{f.label}{f.unit ? ` (${f.unit})` : ""}{f.required ? " *" : ""}</span>;
  if (f.type === "number" || f.type === "measurement") {
    const n = valor === undefined || valor === null || valor === "" ? null : Number(valor);
    const passo = (d: number) => onChange(String(Math.max(f.min ?? -Infinity, Math.min(f.max ?? Infinity, Math.round(((n ?? 0) + d) * 100) / 100))));
    return (
      <label className="block">
        {rotulo}
        <span className="flex items-center gap-1.5">
          <button type="button" className="btn-secondary min-h-[2.75rem] min-w-[2.75rem]" disabled={disabled} onClick={() => passo(-1)} aria-label={`Diminuir ${f.label}`}>−</button>
          <input className="input min-h-[2.75rem] w-full text-center text-lg" inputMode="decimal" disabled={disabled} value={valor == null ? "" : String(valor)}
            onChange={(e) => onChange(e.target.value.replace(",", "."))} />
          <button type="button" className="btn-secondary min-h-[2.75rem] min-w-[2.75rem]" disabled={disabled} onClick={() => passo(1)} aria-label={`Aumentar ${f.label}`}>+</button>
          {classe && classe !== "ok" && <span className={COND_STYLE[classe].badge}>{COND_LABEL[classe]}</span>}
        </span>
      </label>
    );
  }
  if (f.type === "choice") {
    return (
      <div>{rotulo}
        <div className="flex flex-wrap gap-1.5">
          {f.options?.map((o) => (
            <button key={o.value} type="button" aria-pressed={valor === o.value} disabled={disabled} onClick={() => onChange(valor === o.value ? "" : o.value)}
              className={`min-h-[2.5rem] rounded-lg border px-3 text-sm ${valor === o.value ? "border-tm-600 bg-tm-50 font-medium text-tm-700" : "border-surface-border"}`}>{o.label}</button>
          ))}
        </div>
      </div>
    );
  }
  if (f.type === "multichoice") {
    const sel = Array.isArray(valor) ? (valor as string[]) : [];
    return (
      <div>{rotulo}
        <div className="flex flex-wrap gap-1.5">
          {f.options?.map((o) => (
            <button key={o.value} type="button" aria-pressed={sel.includes(o.value)} disabled={disabled}
              onClick={() => onChange(sel.includes(o.value) ? sel.filter((x) => x !== o.value) : [...sel, o.value])}
              className={`min-h-[2.5rem] rounded-lg border px-3 text-sm ${sel.includes(o.value) ? "border-tm-600 bg-tm-50 font-medium text-tm-700" : "border-surface-border"}`}>{o.label}</button>
          ))}
        </div>
      </div>
    );
  }
  if (f.type === "boolean") {
    return (
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={valor === true} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        {f.label}
      </label>
    );
  }
  return (
    <label className="block">{rotulo}
      <input className="input w-full" type={f.type === "date" ? "date" : "text"} disabled={disabled} maxLength={200}
        value={valor == null ? "" : String(valor)} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function corpo(positionKey: string, f: Form) {
  const limpa = (m: Record<string, unknown>) => Object.fromEntries(Object.entries(m).filter(([, v]) => v !== "" && v !== undefined));
  const estado = (e: Form["before"]) => ({ ...(e.condition ? { condition: e.condition } : {}), measures: limpa(e.measures) });
  return {
    positionKey,
    condition: f.condition,
    interventions: f.interventions,
    measures: limpa(f.measures),
    before: f.beforeAfter ? estado(f.before) : null,
    after: f.beforeAfter ? estado(f.after) : null,
    notes: f.notes.trim() || null,
    pendingReason: f.interventions.includes("pending") ? f.pendingReason.trim() || null : null,
    pendingOwner: f.interventions.includes("pending") ? f.pendingOwner.trim() || null : null,
    pendingDueAt: f.interventions.includes("pending") && f.pendingDueAt ? new Date(`${f.pendingDueAt}T12:00:00`).toISOString() : null,
  };
}
